import {getDeviceType} from "../../utils/versionUtils"

export interface TableRowData {
	macAddress: string
	connectionStatus: "connected" | "disconnected" | "unknown"
	lastSeen?: string
	dataSize?: number
	softwareVersion: string
	fwStatus: string
	radarFirmwareVersions: string
	radarFirmwareStatus: boolean[] | null
	radarFwStatus: string
	shadowClassic: any
	shadowRadar: any
	uploadStatus?: string
	uploadError?: string
}

export interface DeviceTableProps {
	data: TableRowData[]
	onRefresh: (deviceId: string) => void
	selectedDevices: string[]
	onDeviceSelectionChange: (deviceIds: string[]) => void
}

export type DeviceTypeFilter = "all" | "angel" | "falcon" | "favorites"

export type DeviceType = "angel" | "falcon" | "unknown"

export interface RadarVersionEntry {
	text: string
	isLatest: boolean
}

interface DeviceTableToolbarProps {
	data: TableRowData[]
	filteredData: TableRowData[]
	deviceTypeFilter: DeviceTypeFilter
	selectedDevices: string[]
	favorites: string[]
	onFilterChange: (filter: DeviceTypeFilter) => void
	onAddFavorites: (ids: string[]) => void
	onRemoveFavorites: (ids: string[]) => void
}

export const DeviceTableToolbar = ({
	data,
	filteredData,
	deviceTypeFilter,
	selectedDevices,
	favorites,
	onFilterChange,
	onAddFavorites,
	onRemoveFavorites,
}: DeviceTableToolbarProps) => {
	const deviceTypeCounts = {
		angel: data.filter((row) => getDeviceType(row.softwareVersion) === "angel").length,
		falcon: data.filter((row) => getDeviceType(row.softwareVersion) === "falcon").length,
	}

	const filters: {key: DeviceTypeFilter; label: string}[] = [
		{key: "all", label: `All (${data.length})`},
		{key: "angel", label: `Angel (${deviceTypeCounts.angel})`},
		{key: "falcon", label: `Falcon (${deviceTypeCounts.falcon})`},
		{key: "favorites", label: `⭐ Favorites (${favorites.length})`},
	]

	return (
		<div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
			<div className="flex items-center justify-between flex-wrap gap-4">
				<div className="flex items-center gap-4">
					<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
						Filter by device type:
					</span>
					<div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
						{filters.map(({key, label}) => (
							<button
								key={key}
								onClick={() => onFilterChange(key)}
								className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
									deviceTypeFilter === key
										? "bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm"
										: "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
								}`}>
								{label}
							</button>
						))}
					</div>
				</div>
				<div className="flex items-center gap-3">
					{selectedDevices.length > 0 && (
						<div className="flex items-center gap-2">
							<button
								onClick={() => onAddFavorites(selectedDevices)}
								className="px-3 py-1 rounded-md text-xs font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/60 transition-colors">
								⭐ Add to favorites
							</button>
							<button
								onClick={() => onRemoveFavorites(selectedDevices)}
								className="px-3 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
								✕ Remove from favorites
							</button>
						</div>
					)}
					<div className="text-xs text-gray-500 dark:text-gray-400">
						Showing {filteredData.length} of {data.length} devices
						{selectedDevices.length > 0 && ` (${selectedDevices.length} selected)`}
					</div>
				</div>
			</div>
		</div>
	)
}
